import { UTCDate } from "map-wald";
import { AggregationFunction, DataMode, TimePeriod } from "../data";
import { TimePeriodUtils } from "./timeperiod-utils";

interface TimesteppedData {
  date:UTCDate;
}

export interface TimesteppedDataOperator<T extends TimesteppedData> {
  recurringTimePeriod(all:T[],timeperiod:TimePeriod): T;
  sum(data:T[]):T;
  min(data:T[],variableAggregation:AggregationFunction):T;
  max(data:T[],variableAggregation:AggregationFunction):T;
  subtract(lhs:T, rhs:T):T;
  average(data:T[],variableAggregation:AggregationFunction):T;
  scale(data:T, scale:number):T;
  divide(lhs:T,rhs:T):T;
  stddev(data:T[],variableAggregation:AggregationFunction):T;
  trend(data:T[],variableAggregation:AggregationFunction):T;
}

// @dynamic
export class DataModeUtils {
  static processData<T extends TimesteppedData>(
    dataMode: DataMode,timeperiod:TimePeriod,
    aggregation:AggregationFunction,data:T[],operator:TimesteppedDataOperator<T>):T{
    const normal = DataModeUtils.extractTimePeriod(data,timeperiod,aggregation,operator);
    if(!normal){
      return null;
    }

    if( dataMode === DataMode.NORMAL){
      return normal;
    }

    let timePeriodSummaries:T[];
    if(operator.recurringTimePeriod){
      timePeriodSummaries = [operator.recurringTimePeriod(data,timeperiod)];
    } else {
      const comparableTimePeriods:TimePeriod[] = TimePeriodUtils.makeLikeTimePeriods(
        timeperiod,data[0].date,data[data.length-1].date);

      timePeriodSummaries =
        comparableTimePeriods.map(tp=>DataModeUtils.extractTimePeriod(data,tp,aggregation,operator))
          .filter(tp=>tp);
    }

    if(dataMode===DataMode.MEAN){
      return operator.average(timePeriodSummaries,aggregation);
    }

    if(dataMode===DataMode.ABS_MEAN){
      return operator.subtract(normal,operator.average(timePeriodSummaries,aggregation));
    }

    if(dataMode===DataMode.REL_MEAN){
      return operator.scale(operator.divide(normal,operator.average(timePeriodSummaries,aggregation)),100.0);
      // return tableFunc([normal,operator.average(timePeriodSummaries)],
      //   ([val,mean,_]:number[])=>{
      //     if(mean===0.0){
      //       return NaN;
      //     }
      //     return 100.0*val/mean;
      //   });
    }

    if(dataMode===DataMode.Z_SCORE){
      const mean = operator.average(timePeriodSummaries,aggregation);
      const variance = operator.stddev(timePeriodSummaries,aggregation);
      const result = operator.divide(operator.subtract(normal,mean),variance);
      return result;
    }

    if((dataMode===DataMode.TREND)||(dataMode===DataMode.RELATIVE_TREND)){
      let trend = operator.trend(timePeriodSummaries,aggregation);
      if(dataMode===DataMode.RELATIVE_TREND){
        const mean = operator.average(timePeriodSummaries,aggregation);
        trend = operator.scale(operator.divide(trend,mean),100.0);
      }
      return trend;
    }

    console.log('NOT IMPLEMENTED',dataMode);
    throw 'Not implemented';
  }

  static extractTimePeriod<T extends TimesteppedData>(data:T[],timeperiod:TimePeriod,aggregation:AggregationFunction,operator:TimesteppedDataOperator<T>):T{
    const rows = data.filter(row=>TimePeriodUtils.withinTimePeriod(row.date,timeperiod))
    if(rows.length<=1){
      return rows[0];
    }

    switch(aggregation){
      case 'sum':
        return operator.sum(rows);
      case 'mean':
        return operator.average(rows,aggregation);
      case 'min':
        return operator.min(rows,aggregation);
      case 'max':
        return operator.max(rows,aggregation);
    }
  }
}

